/**
 * Onglet « Tutoriels Vidéo » de /tutoriels (extrait de src/pages/Tutoriels.tsx).
 * Les tutos disponibles se lisent dans le lecteur plein écran ; les autres
 * gardent la fiche « Vidéo à venir » avec renvoi vers l'assistant Posy.
 */
import { useState } from 'react';
import { Bot, Clock, PlayCircle, Video } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { tutoSources, type TutoId } from './tutorial-catalog';
import { TutorialPlayerDialog } from './TutorialPlayerDialog';
import {
  VIDEO_CATEGORIES,
  VIDEO_TUTORIALS,
  categoryLabel,
  type VideoTutorial,
} from './video-catalog';
import { ComingSoonVideoDialog } from './ComingSoonVideoDialog';

type TutorialVideosTabProps = {
  /** Ouvre l'assistant et lui pose la question. */
  onAskPosy: (question: string) => void;
  /** Bascule sur l'onglet assistant. */
  onOpenChat: () => void;
};

function VideoCard({ video, onOpen }: { video: VideoTutorial; onOpen: () => void }) {
  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:border-primary/30 hover:shadow-md"
      onClick={onOpen}
      data-testid={`video-card-${video.id}`}
    >
      {/* Ratio 16:9 par padding-top : la WebView Sunmi ignore aspect-ratio. */}
      <div className="relative w-full bg-gradient-to-br from-primary/10 to-primary/5 pt-[56.25%]">
        {video.tutoId && (
          <img
            src={tutoSources(video.tutoId, false).poster}
            alt=""
            loading="lazy"
            width={320}
            height={180}
            className="absolute left-0 top-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/20 transition-colors group-hover:bg-primary/30">
            <PlayCircle className="h-8 w-8 text-primary" />
          </div>
        </div>
        <Badge className="absolute bottom-2 right-2 bg-background/80 text-[10px] text-foreground backdrop-blur-sm">
          <Clock className="mr-0.5 h-3 w-3" /> {video.duration}
        </Badge>
        <Badge variant="secondary" className="absolute left-2 top-2 text-[10px]">
          {video.tutoId ? categoryLabel(video.category) : 'Vidéo à venir'}
        </Badge>
      </div>
      <CardContent className="space-y-1 p-3">
        <h3 className="line-clamp-2 text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
          {video.title}
        </h3>
        <p className="line-clamp-2 text-xs text-muted-foreground">{video.description}</p>
      </CardContent>
    </Card>
  );
}

function MoreVideosNotice({ onOpenChat }: { onOpenChat: () => void }) {
  return (
    <Card className="border-dashed">
      <CardContent className="space-y-2 py-8 text-center">
        <Video className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">D’autres vidéos arrivent bientôt</p>
        <p className="mx-auto max-w-md text-xs text-muted-foreground">
          En attendant, l’assistant Posy répond à toutes vos questions sur VisionPOS.
        </p>
        <Button variant="outline" size="sm" className="mt-2 gap-1" onClick={onOpenChat}>
          <Bot className="h-4 w-4" /> Poser une question à Posy
        </Button>
      </CardContent>
    </Card>
  );
}

export function TutorialVideosTab({ onAskPosy, onOpenChat }: TutorialVideosTabProps) {
  const [filter, setFilter] = useState<string>('all');
  const [playing, setPlaying] = useState<TutoId | null>(null);
  const [comingSoon, setComingSoon] = useState<VideoTutorial | null>(null);
  const videos =
    filter === 'all' ? VIDEO_TUTORIALS : VIDEO_TUTORIALS.filter((v) => v.category === filter);
  const available = VIDEO_TUTORIALS.filter((v) => v.tutoId).length;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Video className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Tutoriels Vidéo</h1>
            <p className="text-sm text-muted-foreground">
              {available} tutos d’une minute pour maîtriser VisionPOS
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {VIDEO_CATEGORIES.map((cat) => (
            <Button
              key={cat.id}
              variant={filter === cat.id ? 'default' : 'outline'}
              size="sm"
              className="text-xs"
              onClick={() => setFilter(cat.id)}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {videos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onOpen={() => (video.tutoId ? setPlaying(video.tutoId) : setComingSoon(video))}
            />
          ))}
        </div>

        <MoreVideosNotice onOpenChat={onOpenChat} />
      </div>

      <TutorialPlayerDialog tutoId={playing} onClose={() => setPlaying(null)} />
      <ComingSoonVideoDialog
        video={comingSoon}
        onClose={() => setComingSoon(null)}
        onAskPosy={(title) => {
          setComingSoon(null);
          onAskPosy(`Explique-moi en détail : ${title}`);
        }}
      />
    </div>
  );
}
